import React, { useState } from 'react';
import { useGame, LEVELS } from './GameContext';
import LogicPuzzle from './minigames/LogicPuzzle';
import SafetyGearQuiz from './minigames/SafetyGearQuiz';
import ToolDragDrop from './minigames/ToolDragDrop';
import ScenarioDecision from './minigames/ScenarioDecision';
import ToolTimingChallenge from './minigames/ToolTimingChallenge';
import FixMachinePuzzle from './minigames/FixMachinePuzzle';
import ShiftReadinessQuiz from './minigames/ShiftReadinessQuiz';

const MINIGAME_COMPONENTS = {
  puzzle: LogicPuzzle,
  quiz: SafetyGearQuiz,
  drag_drop: ToolDragDrop,
  scenario: ScenarioDecision,
  timed_choice: ToolTimingChallenge,
  machine_puzzle: FixMachinePuzzle,
  shift_quiz: ShiftReadinessQuiz,
  // Add more mappings as you create new mini-games
};

const API_URL = process.env.REACT_APP_API_URL;

function LevelScreen({ onBack }) {
  const { state, dispatch } = useGame();
  const level = LEVELS.find(l => l.id === state.currentLevel);
  const allTasksDone = level.tasks.every(t => state.completedTasks[t.id]);
  const missionDone = state.completedMissions[level.majorMission.id];
  const badgeEarned = state.badges.includes(level.badge.id);

  const [activeTask, setActiveTask] = useState(null);

  const handleCompleteTask = (task) => {
    setActiveTask(task);
  };

  const handleMiniGameComplete = async (result) => {
    if (activeTask) {
      // Award reward points via backend
      try {
        const user = JSON.parse(localStorage.getItem('gogig_user'));
        const candidateId = user && user.id;
        if (candidateId) {
          await fetch(`${API_URL}/api/reward/game/complete`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              candidate_id: candidateId,
              game_id: activeTask.id,
              xp: result.xp,
              coins: result.coins
            })
          });
        }
      } catch (err) {
        // Optionally handle error (e.g., show notification)
        console.error('Failed to award game reward points:', err);
      }
      dispatch({ type: 'COMPLETE_TASK', payload: { levelId: level.id, task: activeTask, xp: result.xp, coins: result.coins } });
    }
    setActiveTask(null);
  };

  const handleCompleteMission = () => {
    if (!missionDone && allTasksDone) {
      dispatch({ type: 'COMPLETE_MISSION', payload: { levelId: level.id, mission: level.majorMission } });
    }
  };

  return (
    <div style={{ minHeight: '70vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-start', padding: 24 }}>
      <button className="gogig-card-btn-outline" style={{ alignSelf: 'flex-start', marginBottom: 18 }} onClick={onBack}>&larr; Back</button>
      <h2 style={{ fontWeight: 800, fontSize: 26, color: '#22c55e', marginBottom: 8 }}>{level.name}</h2>
      <div style={{ color: '#555', fontSize: 15, marginBottom: 18, textAlign: 'center', maxWidth: 400 }}>{level.description}</div>
      <div style={{ width: '100%', maxWidth: 400, marginBottom: 24 }}>
        <h4 style={{ fontWeight: 700, fontSize: 17, marginBottom: 10, color: '#222' }}>Daily Tasks</h4>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {level.tasks.map(task => {
            const MiniGame = MINIGAME_COMPONENTS[task.type];
            return (
              <div key={task.id} style={{ background: '#fff', border: '1.5px solid #e5e7eb', borderRadius: 10, padding: 14, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                  <div style={{ fontWeight: 600 }}>{task.title}</div>
                  <div style={{ color: '#888', fontSize: 13 }}>{task.type.toUpperCase()} | XP: {task.xpReward} | Coins: {task.coinReward}</div>
                </div>
                {state.completedTasks[task.id] ? (
                  <span style={{ color: '#22c55e', fontWeight: 700 }}>Done</span>
                ) : (
                  <button className="gogig-card-btn" style={{ fontSize: 14, padding: '6px 16px' }} onClick={() => handleCompleteTask(task)}>Play</button>
                )}
                {/* Mini-game modal/inline */}
                {activeTask && activeTask.id === task.id && MiniGame && (
                  <div className="gogig-modal-bg" style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', zIndex: 1000 }} onClick={() => setActiveTask(null)}>
                    <div className="gogig-modal" style={{ minWidth: 320, maxWidth: 400, margin: '60px auto' }} onClick={e => e.stopPropagation()}>
                      {task.type === 'quiz' ? (
                        <MiniGame
                          questions={task.questions}
                          xpReward={task.xpReward}
                          coinReward={task.coinReward}
                          timeLimit={task.timeLimit}
                          onComplete={handleMiniGameComplete}
                        />
                      ) : task.type === 'drag_drop' ? (
                        <MiniGame
                          xpReward={task.xpReward}
                          coinReward={task.coinReward}
                          onComplete={handleMiniGameComplete}
                        />
                      ) : task.type === 'scenario' ? (
                        <MiniGame
                          question={task.questions[0].question}
                          options={task.questions[0].options}
                          correctAnswer={task.questions[0].correctAnswer}
                          xpReward={task.xpReward}
                          coinReward={task.coinReward}
                          onComplete={handleMiniGameComplete}
                        />
                      ) : task.type === 'timed_choice' ? (
                        <MiniGame
                          questions={task.questions}
                          xpReward={task.xpReward}
                          coinReward={task.coinReward}
                          timeLimit={task.timeLimit}
                          onComplete={handleMiniGameComplete}
                        />
                      ) : task.type === 'machine_puzzle' ? (
                        <MiniGame
                          xpReward={task.xpReward}
                          coinReward={task.coinReward}
                          onComplete={handleMiniGameComplete}
                        />
                      ) : task.type === 'shift_quiz' ? (
                        <MiniGame
                          question={task.questions[0].question}
                          options={task.questions[0].options}
                          correctAnswer={task.questions[0].correctAnswer}
                          xpReward={task.xpReward}
                          coinReward={task.coinReward}
                          timeLimit={task.timeLimit}
                          onComplete={handleMiniGameComplete}
                        />
                      ) : (
                        <MiniGame onComplete={() => handleMiniGameComplete({ xp: task.xpReward, coins: task.coinReward })} />
                      )}
                      <button className="gogig-card-btn-outline" style={{ marginTop: 12 }} onClick={() => setActiveTask(null)}>Cancel</button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
      <div style={{ width: '100%', maxWidth: 400, marginBottom: 24 }}>
        <h4 style={{ fontWeight: 700, fontSize: 17, marginBottom: 10, color: '#222' }}>Major Mission</h4>
        <div style={{ background: '#fff', border: '1.5px solid #e5e7eb', borderRadius: 10, padding: 16, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <div style={{ fontWeight: 600 }}>{level.majorMission.title}</div>
            <div style={{ color: '#888', fontSize: 13 }}>XP: {level.majorMission.xpReward} | Coins: {level.majorMission.coinReward}</div>
          </div>
          {missionDone ? (
            <span style={{ color: '#22c55e', fontWeight: 700 }}>Done</span>
          ) : (
            <button className="gogig-card-btn" style={{ fontSize: 14, padding: '6px 16px' }} onClick={handleCompleteMission} disabled={!allTasksDone}>Complete</button>
          )}
        </div>
      </div>
      {badgeEarned && (
        <div style={{ marginTop: 18, background: '#e0f2fe', border: '2px solid #3b82f6', borderRadius: 12, padding: 18, textAlign: 'center', maxWidth: 340 }}>
          <div style={{ fontWeight: 700, color: '#3b82f6', fontSize: 18, marginBottom: 6 }}>Badge Earned!</div>
          <div style={{ fontWeight: 600 }}>{level.badge.name}</div>
          <div style={{ color: '#555', fontSize: 14 }}>{level.badge.description}</div>
        </div>
      )}
      {badgeEarned && level.id < LEVELS.length && (
        <button className="gogig-card-btn" style={{ marginTop: 24 }} onClick={() => window.location.reload()}>Go to Next Level</button>
      )}
    </div>
  );
}

export default LevelScreen; 